/**
 * MobileSheetChrome — Container (SE-5081)
 *
 * Cross-feature foundation primitive consumed by features/scouting,
 * features/watchlist, and features/notes (per SE-5081). Provides the shared
 * Figma chrome (rounded-top dark surface, top-left ✕ chip, optional centered
 * 4×64 drag handle, optional title, scrollable body, optional sticky footer)
 * and the four dismissal paths:
 *
 *   1. ✕ chip tap                 — passes onClose straight to the View.
 *   2. Backdrop tap / Android back — handled by GlueStack Actionsheet via
 *                                    its native onClose plumbing.
 *   3. Swipe-down (native)         — PanGestureHandler in the View; the
 *                                    callback below dismisses when the
 *                                    gesture exceeds either threshold.
 *   4. Escape key (web)            — global keydown listener attached only
 *                                    when isOpen && Platform.OS === "web".
 *
 * The web ESC listener mirrors the SE-5004 precedent in
 * `SeeAllListsDrawerContainer` so two stacked sheets do not get a special
 * propagation guard — both dismissing is acceptable UX.
 * @module components/MobileSheetChrome
 */

import { useCallback, useEffect, type ReactNode } from "react";
import {
  Platform,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from "react-native";
import {
  GestureEvent,
  PanGestureHandlerEventPayload,
} from "react-native-gesture-handler";

import MobileSheetChromeView from "./MobileSheetChromeView";

import { useBottomSheetInset } from "@/hooks/useBottomSheetInset";
import { ifWeb } from "@/utils/platform";

/**
 * Public props for the MobileSheetChrome primitive.
 *
 * The shape here is the stable contract documented in the SE-5081 arch plan
 * §2 — consumed by SE-5074/5076/5077/5078. Renaming or removing fields is a
 * breaking change for those consumers; adding optional fields later is safe.
 */
export interface MobileSheetChromeProps {
  /** Whether the sheet is open. Drives Actionsheet open state. */
  readonly isOpen: boolean;
  /**
   * Dismissal handler. Fired by ✕ chip tap, backdrop tap, swipe-down past
   * threshold, ESC on web, and Android back. The receiver must NOT assume a
   * selection happened — this is a pure "close" signal.
   */
  readonly onClose: () => void;
  /** Optional sheet title rendered below the ✕ chip. Truncates with ellipsis. */
  readonly title?: string;
  /** Optional one-line explanation under the title. Wraps rather than truncating. */
  readonly subtitle?: string;
  /**
   * Render the 4×64 drag handle centered at the top. Defaults to true. Pass
   * false for full-screen sheet variants (e.g. SE-5077 Share).
   */
  readonly showHandle?: boolean;
  /**
   * Optional node pinned in the sticky header band, below the title (e.g. a
   * search bar). Stays above the scrolling body. SE-5401.
   */
  readonly headerContent?: ReactNode;
  /**
   * Draw the hairline divider above the sticky footer. Defaults to true
   * (the pre-SE-5401 behavior, preserved for existing consumers such as the
   * Share sheet). The All-lists sheet opts OUT (SE-5401: the Figma "All
   * lists" sheet has no dividing line in the sheet sector).
   */
  readonly showFooterDivider?: boolean;
  /** Sheet body — rendered inside a vertically-scrolling region. */
  readonly children: ReactNode;
  /**
   * Optional sticky footer slot. Stays anchored to the bottom of the sheet;
   * body scrolls behind it. Typical use: "Apply" / "Add N selected" buttons.
   */
  readonly footer?: ReactNode;
  /**
   * Disable the gesture dismissal paths (native swipe-down AND the
   * pull-to-dismiss overscroll) while keeping backdrop, ✕ chip, ESC and
   * Android back intact. For sheets whose body owns vertical pan gestures
   * (e.g. a drag-to-reorder list) that would contend with the chrome's
   * content-wide PanGestureHandler. Defaults to false.
   */
  readonly disableSwipeDismiss?: boolean;
}

/**
 * Swipe-down dismissal threshold (Figma spec): ≥100px translation OR
 * ≥0.5 velocity. Encoded as constants so both the test and the production
 * path read from the same source.
 */
const SWIPE_DOWN_TRANSLATION_THRESHOLD_PX = 100;
const SWIPE_DOWN_VELOCITY_THRESHOLD = 0.5;

/**
 * Pull-to-dismiss overscroll threshold (SE-5401): when the body is pulled
 * down past the top by more than this many px (a negative contentOffset.y),
 * the sheet dismisses. This is the "Modal should close when scrolling down"
 * gesture and — unlike the native-only PanGestureHandler swipe — works on web
 * too (the surface QA reported against), where `handleSwipeDown` is gated off.
 */
const PULL_TO_DISMISS_OVERSCROLL_PX = 64;

/**
 * Cross-feature mobile bottom-sheet primitive.
 * @param props - Component props
 * @param props.isOpen - Whether the sheet is currently open
 * @param props.onClose - Unified dismissal callback (all 4 paths)
 * @param props.title - Optional sheet title shown below the ✕ chip
 * @param props.subtitle - Optional one-line explanation under the title
 * @param props.showHandle - Render the centered drag handle (default true)
 * @param props.headerContent - Optional sticky header node below the title (e.g. search bar)
 * @param props.showFooterDivider - Draw the hairline above the footer (default true)
 * @param props.children - Sheet body, rendered inside a ScrollView
 * @param props.footer - Optional sticky footer node
 * @param props.disableSwipeDismiss - Gate off gesture dismissal (swipe-down + pull-to-dismiss)
 */
const MobileSheetChromeContainer = ({
  isOpen,
  onClose,
  title,
  subtitle,
  showHandle = true,
  headerContent,
  showFooterDivider = true,
  children,
  footer,
  disableSwipeDismiss = false,
}: MobileSheetChromeProps) => {
  // Native swipe-down handler — gated below via `ifWeb` so the gesture
  // handler is wired only on native platforms. Web dismisses via ESC + the
  // Actionsheet backdrop click.
  const handleSwipeDownNative = useCallback(
    (evt: GestureEvent<PanGestureHandlerEventPayload>) => {
      const { translationY, velocityY } = evt.nativeEvent;
      if (
        translationY > SWIPE_DOWN_TRANSLATION_THRESHOLD_PX ||
        velocityY > SWIPE_DOWN_VELOCITY_THRESHOLD
      ) {
        onClose();
      }
    },
    [onClose]
  );

  // Pull-to-dismiss: dismiss when the body is overscrolled downward past the
  // top by more than the threshold. Works on web AND native (the native swipe
  // handler only fires for pans on the chrome, not from inside the
  // ScrollView), so this is the cross-platform "close when scrolling down".
  const handleScroll = useCallback(
    (evt: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (disableSwipeDismiss) return;
      if (evt.nativeEvent.contentOffset.y <= -PULL_TO_DISMISS_OVERSCROLL_PX) {
        onClose();
      }
    },
    [disableSwipeDismiss, onClose]
  );

  // Sticky-footer bottom clearance. The hook is read here (Container) because
  // the View must stay a hook-free pure arrow, and only the PRIMITIVE presence
  // flag crosses the boundary — the View owns the fixed presentation class it
  // selects from that flag.
  //
  // EXACTLY ONE LAYER APPLIES THE SYSTEM-NAVIGATION INSET, and it is not this
  // one: `ActionsheetContent` pads itself by the captured Android inset via the
  // shared `useBottomSheetInsetStyle` (SE-5151), which lifts everything inside
  // the sheet — this footer included — clear of the nav bar. All the footer
  // needs on top of that is breathing room, so `useBottomSheetInset` is read
  // only as the "is there a nav bar at all" signal, never added to the padding.
  // That is exactly why a boolean suffices here: no device measurement reaches
  // the View, so nothing about the footer's padding is per-device any more.
  //
  // SE-5080 previously added the inset here DOUBLED, on the premise that the
  // gluestack motion view swallowed the Content's own inset padding and rested
  // one inset below the window. That premise no longer holds, and the two
  // layers stacked into 3× the nav bar: measured on the SE-6995 report (Galaxy
  // S24, 1080×2340 @2.8125, 48dp inset = 135px) the sheet box was
  // 8dp + 85vh + 135px tall — i.e. the Content's inset WAS applied — while this
  // footer added a further 2×135 + 16dp, leaving a 320px (114dp) empty band
  // between the buttons and the nav bar. Reproduce the arithmetic before
  // reintroducing any multiplier here.
  const hasFooterNavClearance = useBottomSheetInset() > 0;

  // ESC dismissal — web only. Mirrors SeeAllListsDrawerContainer (SE-5004).
  // The Actionsheet stays in the DOM on web (useRNModal={false}), so a
  // window-level keydown listener can observe Escape presses.
  useEffect(() => {
    if (Platform.OS !== "web" || !isOpen) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, [isOpen, onClose]);

  return (
    <MobileSheetChromeView
      isOpen={isOpen}
      onClose={onClose}
      handleSwipeDown={
        disableSwipeDismiss
          ? undefined
          : ifWeb(undefined, handleSwipeDownNative)
      }
      handleScroll={handleScroll}
      title={title}
      subtitle={subtitle}
      showHandle={showHandle}
      headerContent={headerContent}
      showFooterDivider={showFooterDivider}
      hasFooterNavClearance={hasFooterNavClearance}
      footer={footer}
    >
      {children}
    </MobileSheetChromeView>
  );
};

export default MobileSheetChromeContainer;
